@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');
@import "tailwindcss";

@theme {
  --font-sans: 'Poppins', system-ui, sans-serif;

  /* Connexa brand — ported from mobile src/constants/colors.ts */
  --color-primary:    #FF9C66;
  --color-action:     #1447E6;
  --color-navy:       #050C4A;

  --color-gray-25:    #FCFCFC;
  --color-gray-50:    #F9FAFB;
  --color-gray-100:   #F3F4F6;
  --color-gray-200:   #E5E7EB;
  --color-gray-300:   #D1D5DB;
  --color-gray-400:   #9CA3AF;
  --color-gray-500:   #6B7280;
  --color-gray-600:   #4B5563;
  --color-gray-700:   #374151;
  --color-gray-800:   #1F2937;
  --color-gray-900:   #111827;

  --color-blue-50:    #EFF4FF;
  --color-blue-100:   #D1E0FF;
  --color-blue-300:   #84ADFF;
  --color-blue-500:   #2970FF;
  --color-blue-600:   #155EEF;
  --color-blue-700:   #004EEB;

  --color-orange-100: #FFE6D5;
  --color-orange-300: #FF9C66;
  --color-orange-400: #FF692E;

  --color-success:    #3CCB7F;
  --color-error:      #EF4444;
}

*, *::before, *::after {
  box-sizing: border-box;
}

body {
  margin: 0;
  font-family: var(--font-sans);
  background-color: #F3F4F6;
  color: #050C4A;
  -webkit-font-smoothing: antialiased;
}

/* ─── Component classes ────────────────────────────────────────────────── */

@layer components {
  /* Buttons — base + filled/outlined variants. Default size matches the
     primary CTA (rounded-2xl, py-3.5). Use .btn-sm for the smaller modal
     variant (rounded-xl, py-3). */
  .btn {
    @apply inline-flex items-center justify-center gap-2 w-full
           rounded-2xl py-3.5 text-sm font-semibold
           transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed;
  }
  .btn-sm {
    @apply rounded-xl py-3;
  }
  .btn-primary {
    @apply bg-action text-white hover:bg-blue-700;
  }
  .btn-outline {
    @apply border-2 border-action text-action hover:bg-blue-50;
  }
  .btn-icon {
    @apply p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors;
  }

  /* Page shell — outer chrome around every route */
  .shell-root {
    @apply min-h-screen bg-gray-100 flex items-start justify-center py-6 px-4;
  }
  .shell-card {
    @apply w-full max-w-sm bg-white rounded-2xl shadow-lg overflow-hidden;
  }
  .shell-header {
    @apply flex items-center justify-center py-6 border-b border-gray-100;
  }
  .shell-logo {
    @apply h-8 w-auto;
  }

  /* Page content block — the main area below the shell header */
  .page-content {
    @apply bg-gray-50 pb-6;
  }
  .page-cta-stack {
    @apply px-4 flex flex-col gap-3;
  }

  /* Card — used for profile + business card content */
  .card {
    @apply mx-4 my-4 bg-white rounded-2xl border border-gray-100 shadow-sm px-6 py-6;
  }

  /* Avatar — picture or initials placeholder, 96px (matches live design) */
  .avatar-img {
    @apply w-24 h-24 rounded-full object-cover shrink-0;
  }
  .avatar-placeholder {
    @apply w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center shrink-0;
  }
  .avatar-initials {
    @apply text-2xl font-semibold text-navy;
  }

  /* Profile heading — owner full-name in cards */
  .profile-name {
    @apply text-xl font-semibold text-navy text-center leading-tight;
  }
  .profile-headline {
    @apply text-sm text-gray-500 text-center mt-1;
  }
  .profile-card-body {
    @apply flex flex-col items-center gap-3 px-6 py-8;
  }
  .profile-card-socials {
    @apply flex items-center justify-center gap-3 mt-1;
  }

  /* Form fields */
  .field {
    @apply flex flex-col gap-1;
  }
  .field-label {
    @apply text-xs font-medium text-gray-600;
  }
  .field-required {
    @apply text-error ml-0.5;
  }
  .field-input {
    @apply rounded-xl border border-gray-200 px-3 py-2 text-sm
           text-navy placeholder:text-gray-400
           focus:outline-none focus:ring-2 focus:ring-blue-500;
  }
  .field-textarea {
    @apply rounded-xl border border-gray-200 px-3 py-2 text-sm
           text-navy placeholder:text-gray-400 resize-none
           focus:outline-none focus:ring-2 focus:ring-blue-500;
  }

  /* State block — empty/error/expired/success pages share this layout */
  .state {
    @apply flex flex-col items-center gap-3 py-16 text-center px-6;
  }
  .state-title {
    @apply font-semibold text-navy;
  }
  .state-desc {
    @apply text-sm text-gray-500;
  }

  /* Status bubble — round colored icon container, 56px */
  .status-bubble {
    @apply w-14 h-14 rounded-full flex items-center justify-center;
  }
  .status-bubble-gray   { @apply bg-gray-100 text-gray-400; }
  .status-bubble-amber  { @apply bg-amber-50 text-amber-400; }
  .status-bubble-green  { @apply bg-green-50 text-success; }

  /* Spinner — neutral track + brand accent on top, used in loading states */
  .spinner {
    @apply w-8 h-8 border-2 border-gray-200 border-t-action rounded-full animate-spin;
  }
  .spinner-wrap {
    @apply flex flex-col items-center gap-3 py-16 text-gray-400;
  }
  .spinner-wrap-text {
    @apply text-sm;
  }

  /* Modal */
  .modal-backdrop {
    @apply fixed inset-0 z-50 flex items-end sm:items-center justify-center
           p-4 bg-black/40 backdrop-blur-sm;
  }
  .modal-container {
    @apply w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden;
  }
  .modal-header {
    @apply flex items-center justify-between px-6 py-4 border-b border-gray-100;
  }
  .modal-title {
    @apply text-base font-semibold text-navy;
  }
  .modal-body {
    @apply px-6 py-5 flex flex-col gap-4;
  }

  /* Social-link pill in profile cards */
  .social-pill {
    @apply flex items-center justify-center w-8 h-8 rounded-full
           bg-gray-100 text-gray-500
           hover:bg-blue-50 hover:text-action transition-colors;
  }

  /* App store badge grid */
  .store-grid {
    @apply grid grid-cols-2 gap-3 w-full;
  }
  .store-link {
    @apply hover:opacity-80 active:scale-95 transition-all;
  }
  .store-img {
    @apply w-full h-auto select-none;
  }

  /* Contact rows on profile access page */
  .contact-row {
    @apply flex items-center gap-3 text-sm;
  }
  .contact-row-icon {
    @apply text-gray-400 shrink-0;
  }
  .contact-row-label {
    @apply truncate;
  }
}
